import React, { useState, useCallback, useMemo, useRef } from "react";
import ReactDOM from "react-dom";
import { apiCall } from "../../_services/apiCall";
import config from "../../config/config";
import TagFilterBar from "./Components/TagFilterBar";
import Spinner from "../../globalComponents/ui/Spinner";
import SimpleDropdown from "../../globalComponents/form/dropdowns/SimpleDropdown";
import ViewOnlyBanner from "./Components/ViewOnlyBanner";
import { Modal, ModalHeader } from "../../globalComponents/ui/Modal";
import { PrimaryButton, SecondaryButton } from "../../globalComponents/ui/Button";
import { getLatestFY } from "../DataManagment/Quantative/component/answerHistoryPage/components/utils";
import { parseTags, questionMatchesSteps } from "./Components/TagFilterUtils";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getTagsFlat(kpi) {
  const combos = parseTags(kpi.tags);
  const seen = new Set(); const result = [];
  for (const combo of combos)
    for (const t of combo) {
      if (!t?.key || !t?.value) continue;
      const k = `${t.key}:::${t.value}`;
      if (!seen.has(k)) { seen.add(k); result.push(t); }
    }
  return result;
}

// ─── Tooltip ──────────────────────────────────────────────────────────────────
// Uses a fixed-position div rendered to document.body via portal.
// Tracks mouse position directly so it never flickers when moving over children.

function Tooltip({ text, children }) {
  const [pos, setPos] = useState(null); // null = hidden
  const containerRef = useRef(null);

  const handleMouseMove = (e) => {
    setPos({ x: e.clientX, y: e.clientY });
  };

  const handleMouseLeave = (e) => {
    // Only hide if we actually left the container (not just moved to a child)
    if (containerRef.current && !containerRef.current.contains(e.relatedTarget)) {
      setPos(null);
    }
  };

  return (
    <>
      <span
        ref={containerRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{ display: "inline-flex" }}
      >
        {children}
      </span>
      {pos && typeof document !== "undefined" && ReactDOM.createPortal(
        <div style={{
          position: "fixed",
          top: pos.y + 14,
          left: pos.x + 8,
          zIndex: 99999,
          background: "#1e293b",
          color: "#f8fafc",
          fontSize: 11,
          fontWeight: 500,
          padding: "6px 10px",
          borderRadius: 7,
          boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
          maxWidth: 300,
          lineHeight: 1.5,
          pointerEvents: "none",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}>
          {text}
        </div>,
        document.body
      )}
    </>
  );
}

// ─── Apply-All Modal ───────────────────────────────────────────────────────────

function ApplyAllModal({ filteredKpis, onClose, onApply }) {
  const [evidences, setEvidences] = useState([]);
  const [inputVal, setInputVal] = useState("");
  const inputRef = useRef(null);

  const add = () => {
    const v = inputVal.trim();
    if (!v || evidences.includes(v)) return;
    setEvidences(p => [...p, v]);
    setInputVal("");
    inputRef.current?.focus();
  };

  const handleApply = () => {
    if (!evidences.length) return;
    onApply(evidences);
  };

  return (
    <Modal show onClose={onClose} maxWidth={480}>
      <ModalHeader
        icon="fas fa-layer-group"
        title="Apply Evidences to All Filtered KPIs"
        subtitle={`Stamps the same evidences onto ${filteredKpis.length} KPI${filteredKpis.length !== 1 ? "s" : ""} — replaces existing`}
        onClose={onClose}
      />
      <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 10, padding: "10px 14px", fontSize: 12, color: "#0c4a6e" }}>
          <i className="fas fa-info-circle" style={{ marginRight: 6 }} />
          This will <strong>replace</strong> existing evidences on all {filteredKpis.length} currently visible KPIs. Use filters to narrow the scope first.
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 700, color: "#475569", display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.4px" }}>
            Add Evidence
          </label>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              ref={inputRef}
              value={inputVal}
              onChange={e => setInputVal(e.target.value)}
              onKeyDown={e => e.key === "Enter" && (e.preventDefault(), add())}
              placeholder="Type an evidence and press Enter…"
              autoFocus
              style={{
                flex: 1, padding: "9px 12px", borderRadius: 8,
                border: "1.5px solid #e2e8f0", fontSize: 13, outline: "none", color: "#1e293b",
              }}
              onFocus={e => e.target.style.borderColor = "#93c5fd"}
              onBlur={e => e.target.style.borderColor = "#e2e8f0"}
            />
            <button onClick={add} disabled={!inputVal.trim()} style={{
              padding: "9px 16px", borderRadius: 8, border: "none",
              background: inputVal.trim() ? "#3f88a5" : "#e2e8f0",
              color: inputVal.trim() ? "#fff" : "#94a3b8",
              cursor: inputVal.trim() ? "pointer" : "default",
              fontSize: 13, fontWeight: 600, flexShrink: 0,
            }}>Add</button>
          </div>
        </div>
        {evidences.length > 0 ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {evidences.map((ev, i) => (
              <span key={i} style={{
                display: "inline-flex", alignItems: "center", gap: 5,
                padding: "4px 10px", borderRadius: 20,
                background: "#e0f2fe", border: "1px solid #bae6fd",
                fontSize: 12, fontWeight: 500, color: "#0369a1",
              }}>
                {ev}
                <button onClick={() => setEvidences(p => p.filter((_, j) => j !== i))}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "#0369a1", padding: 0, fontSize: 11, lineHeight: 1, opacity: 0.7 }}
                  onMouseEnter={e => e.currentTarget.style.opacity = "1"}
                  onMouseLeave={e => e.currentTarget.style.opacity = "0.7"}
                ><i className="fas fa-times" /></button>
              </span>
            ))}
          </div>
        ) : (
          <div style={{ padding: "16px", textAlign: "center", border: "1.5px dashed #e2e8f0", borderRadius: 10 }}>
            <div style={{ fontSize: 12, color: "#94a3b8" }}>No evidences added yet.</div>
          </div>
        )}
      </div>
      <div style={{ padding: "12px 24px 20px", borderTop: "1px solid #f1f5f9", display: "flex", justifyContent: "flex-end", gap: 10 }}>
        <SecondaryButton onClick={onClose} style={{ minWidth: 80 }}>Cancel</SecondaryButton>
        <PrimaryButton onClick={handleApply} disabled={!evidences.length} style={{ minWidth: 160 }}>
          {`Apply to ${filteredKpis.length} KPI${filteredKpis.length !== 1 ? "s" : ""}`}
        </PrimaryButton>
      </div>
    </Modal>
  );
}

// ─── Evidence Pill ─────────────────────────────────────────────────────────────

function EvidencePill({ label, onRemove, disabled }) {
  return (
    <Tooltip text={label}>
      <span style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        padding: "3px 8px 3px 10px", borderRadius: 20,
        background: "#e0f2fe", border: "1px solid #bae6fd",
        fontSize: 11.5, fontWeight: 500, color: "#0369a1",
        whiteSpace: "nowrap", maxWidth: 260,
      }}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
        {!disabled && (
          <button
            onMouseDown={e => { e.preventDefault(); onRemove(); }}
            style={{
              background: "none", border: "none", cursor: "pointer",
              color: "#7dd3fc", padding: "0 1px", fontSize: 10,
              lineHeight: 1, display: "flex", alignItems: "center", flexShrink: 0,
              transition: "color 0.12s",
            }}
            onMouseEnter={e => e.currentTarget.style.color = "#0369a1"}
            onMouseLeave={e => e.currentTarget.style.color = "#7dd3fc"}
          >
            <i className="fas fa-times" />
          </button>
        )}
      </span>
    </Tooltip>
  );
}

// ─── KPI Row — fully inline, no accordion ─────────────────────────────────────

function KpiEvidenceRow({ kpi, pendingEvidences, onPendingChange, isLast, disabled }) {
  const [addInput, setAddInput] = useState("");
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);

  const evidences = pendingEvidences ?? kpi.requiredEvidences ?? [];
  const saved = kpi.requiredEvidences ?? [];
  const isDirty = pendingEvidences !== null &&
    pendingEvidences !== undefined &&
    (pendingEvidences.length !== saved.length ||
      pendingEvidences.some((v, i) => v !== saved[i]));
  const tags = useMemo(() => getTagsFlat(kpi), [kpi]);

  const addEvidence = () => {
    const v = addInput.trim();
    if (!v) return;
    if (evidences.includes(v)) { setAddInput(""); return; }
    onPendingChange([...evidences, v]);
    setAddInput("");
  };

  const removeEvidence = (idx) => onPendingChange(evidences.filter((_, i) => i !== idx));

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 0,
      borderBottom: isLast ? "none" : "1px solid #f1f5f9",
      background: isDirty ? "#fffbeb" : "#fff",
      transition: "background 0.1s",
      padding: "11px 18px",
    }}>

      {/* ── Left: title + tags (fixed width) ── */}
      <div style={{ width: 260, flexShrink: 0, paddingRight: 16 }}>
        <div style={{
          fontSize: 12.5, fontWeight: 500, color: "#1e293b",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          marginBottom: tags.length ? 4 : 0,
        }} title={kpi.title}>
          {kpi.title}
        </div>
        {tags.length > 0 && (
          <div style={{ display: "flex", gap: 3, flexWrap: "nowrap", overflow: "hidden", alignItems: "center" }}>
            {tags.slice(0, 2).map((t, i) => (
              <span key={i} style={{
                fontSize: 10, fontWeight: 500,
                color: "#0c6b8e", background: "#e0f2fe",
                border: "1px solid #bae6fd", borderRadius: 4,
                padding: "1px 6px", whiteSpace: "nowrap",
                overflow: "hidden", textOverflow: "ellipsis", maxWidth: 110,
              }} title={t.value}>{t.value}</span>
            ))}
            {tags.length > 2 && (
              <Tooltip text={tags.slice(2).map(t => t.value).join("\n")}>
                <span style={{
                  fontSize: 10, fontWeight: 600, color: "#0c6b8e",
                  background: "#e0f2fe", border: "1px solid #bae6fd",
                  borderRadius: 4, padding: "1px 6px",
                  whiteSpace: "nowrap", flexShrink: 0, cursor: "default",
                }}>+{tags.length - 2}</span>
              </Tooltip>
            )}
          </div>
        )}
      </div>

      {/* ── Right: pills + inline add-input ── */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexWrap: "wrap", gap: 5, alignItems: "center", justifyContent: "flex-end" }}>

        {/* Existing pills */}
        {evidences.map((ev, i) => (
          <EvidencePill key={i} label={ev} onRemove={() => removeEvidence(i)} disabled={disabled} />
        ))}

        {/* Inline add input — always visible when not disabled */}
        {!disabled && (
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <input
              ref={inputRef}
              value={addInput}
              onChange={e => setAddInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addEvidence(); } }}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              placeholder={evidences.length === 0 ? "Add evidence…" : "+"}
              style={{
                padding: "3px 8px",
                width: focused || addInput ? 160 : (evidences.length === 0 ? 120 : 28),
                borderRadius: 20,
                border: `1.5px solid ${focused ? "#93c5fd" : "#e2e8f0"}`,
                fontSize: 11.5,
                outline: "none",
                color: "#1e293b",
                background: focused ? "#fff" : "#f8fafc",
                transition: "width 0.2s ease, border-color 0.15s",
                cursor: "text",
              }}
            />
            {(focused || addInput) && (
              <button
                onMouseDown={e => { e.preventDefault(); addEvidence(); }}
                style={{
                  width: 22, height: 22, borderRadius: "50%", border: "none",
                  background: addInput.trim() ? "#3f88a5" : "#e2e8f0",
                  color: addInput.trim() ? "#fff" : "#94a3b8",
                  cursor: addInput.trim() ? "pointer" : "default",
                  fontSize: 10, display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0, transition: "all 0.12s",
                }}
              >
                <i className="fas fa-plus" />
              </button>
            )}
          </div>
        )}

        {/* Unsaved indicator */}
        {isDirty && (
          <span style={{
            fontSize: 10, fontWeight: 700, color: "#d97706",
            background: "#fef3c7", border: "1px solid #fcd34d",
            borderRadius: 4, padding: "1px 6px", whiteSpace: "nowrap",
          }}>Unsaved</span>
        )}
      </div>
    </div>
  );
}

// ─── Main ──────────────────────────────────────────────────────────────────────

export default function KpiRequiredEvidences() {
  const [allKpis, setAllKpis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const [pendingMap, setPendingMap] = useState(new Map());
  const [filterSteps, setFilterSteps] = useState([]);
  const [search, setSearch] = useState("");
  const [showApplyAll, setShowApplyAll] = useState(false);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fyData = useMemo(() => { try { return JSON.parse(localStorage.getItem("financialYears")) ?? []; } catch { return []; } }, []);
  const latestFY = useMemo(() => getLatestFY(fyData), [fyData]);
  const [selectedFY, setSelectedFY] = useState(() => latestFY);
  const isViewOnly = selectedFY?.id !== latestFY?.id;

  const fetchData = useCallback(async () => {
    if (!selectedFY?.id) return;
    setLoading(true);
    try {
      const [settingsRes, unitSettingsRes, evidenceRes] = await Promise.all([
        apiCall(`${config.POSTLOGIN_API_URL_COMPANY}emissionKpiSettings`, {}, { fyid: selectedFY.id }, "GET"),
        apiCall(`${config.POSTLOGIN_API_URL_COMPANY}getUnitKpiSettings`, {}, { fyid: selectedFY.id }, "GET"),
        apiCall(`${config.POSTLOGIN_API_URL_COMPANY}settings/kpis/required-evidences`, {}, { fyid: selectedFY.id }, "GET"),
      ]);

      let kpiList = [];
      if (unitSettingsRes?.isSuccess && unitSettingsRes?.data?.data) {
        const rawSettings = settingsRes?.data;
        const allUnitKpis = unitSettingsRes.data.data;
        if (rawSettings && Array.isArray(rawSettings.kpiIds)) {
          const enabled = new Set(rawSettings.kpiIds.map(Number));
          kpiList = allUnitKpis.filter(k => enabled.has(Number(k.kpiId)));
        }
      }

      const evMap = new Map();
      for (const row of (evidenceRes?.data?.data ?? [])) {
        evMap.set(Number(row.kpiId), row.requiredEvidences ?? []);
      }

      setAllKpis(kpiList.map(kpi => ({
        ...kpi,
        requiredEvidences: evMap.get(Number(kpi.kpiId)) ?? [],
      })));
      setPendingMap(new Map());
    } catch {
      showToast("Error loading data", "error");
    } finally {
      setLoading(false);
    }
  }, [selectedFY?.id]);

  React.useEffect(() => {
    setFilterSteps([]);
    setSearch("");
    setPendingMap(new Map());
    fetchData();
  }, [fetchData]);

  const filteredKpis = useMemo(() => {
    let list = allKpis.filter(k => questionMatchesSteps(k, filterSteps));
    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter(k => k.title?.toLowerCase().includes(s));
    }
    return list;
  }, [allKpis, filterSteps, search]);

  const hasPending = pendingMap.size > 0;

  const configuredCount = useMemo(() =>
    allKpis.filter(k => {
      const evs = pendingMap.has(k.kpiId) ? pendingMap.get(k.kpiId) : k.requiredEvidences;
      return evs?.length > 0;
    }).length,
    [allKpis, pendingMap]
  );

  const setPendingForKpi = (kpiId, evidences) => {
    setPendingMap(prev => {
      const kpi = allKpis.find(k => k.kpiId === kpiId);
      const saved = kpi?.requiredEvidences ?? [];
      const matchesSaved =
        evidences.length === saved.length &&
        evidences.every((v, i) => v === saved[i]);
      const next = new Map(prev);
      if (matchesSaved) {
        next.delete(kpiId);
      } else {
        next.set(kpiId, evidences);
      }
      return next;
    });
  };

  const handleSaveAll = async () => {
    if (!pendingMap.size) return;
    setSaving(true);
    const mappings = Array.from(pendingMap.entries()).map(([kpiId, requiredEvidences]) => ({
      kpiId: Number(kpiId), requiredEvidences,
    }));
    try {
      const { isSuccess, data } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}settings/kpis/required-evidences`, {},
        { fyid: selectedFY.id, mappings }, "POST"
      );
      if (isSuccess) {
        setAllKpis(prev => prev.map(k =>
          pendingMap.has(k.kpiId) ? { ...k, requiredEvidences: pendingMap.get(k.kpiId) } : k
        ));
        setPendingMap(new Map());
        showToast(`${mappings.length} KPI${mappings.length !== 1 ? "s" : ""} saved`);
      } else {
        showToast(data?.message || "Failed to save", "error");
      }
    } catch {
      showToast("Error saving evidences", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleApplyAll = (evidences) => {
    setPendingMap(prev => {
      const next = new Map(prev);
      filteredKpis.forEach(kpi => {
        const saved = kpi.requiredEvidences ?? [];
        const matchesSaved =
          evidences.length === saved.length &&
          evidences.every((v, i) => v === saved[i]);
        if (matchesSaved) {
          next.delete(kpi.kpiId);
        } else {
          next.set(kpi.kpiId, evidences);
        }
      });
      return next;
    });
    setShowApplyAll(false);
  };

  if (loading) {
    return (
      <div style={{ padding: 100, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
        <Spinner size={32} color="#3f88a5" />
        <span style={{ fontSize: 13, color: "#94a3b8" }}>Loading KPI evidence settings…</span>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", background: "#f1f5f9", minHeight: "100vh" }}>
      <style>{`* { box-sizing: border-box; } select:focus { outline: none; }`}</style>

      {toast && (
        <div style={{
          position: "fixed", bottom: 24, right: 24,
          background: toast.type === "error" ? "#dc2626" : "#16a34a",
          color: "#fff", padding: "11px 18px", borderRadius: 10,
          zIndex: 2000, fontSize: 13, fontWeight: 600,
          boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
          display: "flex", alignItems: "center", gap: 8,
        }}>
          {toast.type === "error" ? "✕" : "✓"} {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={{
        background: "#fff", padding: "0 28px", height: 56,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        borderBottom: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 8, height: 28, borderRadius: 4, background: "linear-gradient(to bottom, #3f88a5, #2563eb)" }} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>Required Evidences</div>
            <div style={{ fontSize: 11, color: "#94a3b8" }}>
              {allKpis.length} active KPI{allKpis.length !== 1 ? "s" : ""} · {configuredCount} with evidences
              {hasPending && !isViewOnly && <span style={{ color: "#d97706", fontWeight: 600 }}> · {pendingMap.size} unsaved</span>}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <SimpleDropdown
            options={fyData.map(fy => ({ value: fy.id, label: `${fy.financial_year_value}${fy.id === latestFY?.id ? " (Current)" : ""}` }))}
            value={selectedFY?.id || ""}
            onChange={val => { const f = fyData.find(fy => fy.id === Number(val)); if (f) setSelectedFY(f); }}
            isAlert={selectedFY?.id !== latestFY?.id}
            style={{ width: 180 }}
          />
          {!isViewOnly && (
            <PrimaryButton onClick={handleSaveAll} disabled={!hasPending || saving} style={{ minWidth: 110 }}>
              {saving ? <><Spinner size={12} color="#fff" /> Saving…</> : "Save All"}
            </PrimaryButton>
          )}
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: "24px", maxWidth: 1100, margin: "0 auto" }}>
        {isViewOnly && <ViewOnlyBanner fyValue={selectedFY?.financial_year_value} />}

        {/* Filter strip */}
        <div style={{ background: "#fff", padding: "16px", borderRadius: 12, border: "1px solid #e2e8f0", marginBottom: 14, boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
          <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
            <div style={{ position: "relative", flex: "1 1 220px", minWidth: 0 }}>
              <span style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: "#94a3b8" }}>⌕</span>
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search KPIs by title…"
                style={{
                  width: "100%", padding: "9px 12px 9px 32px",
                  border: "1.5px solid #e2e8f0", borderRadius: 9,
                  fontSize: 13, outline: "none", color: "#1e293b",
                }}
                onFocus={e => e.target.style.borderColor = "#93c5fd"}
                onBlur={e => e.target.style.borderColor = "#e2e8f0"}
              />
            </div>
            {filteredKpis.length > 0 && !isViewOnly && (
              <button
                onClick={() => setShowApplyAll(true)}
                style={{
                  padding: "9px 18px", borderRadius: 8, border: "1.5px solid #bae6fd",
                  background: "#f0f9ff", cursor: "pointer", fontSize: 12, fontWeight: 700,
                  color: "#0369a1", display: "flex", alignItems: "center", gap: 6, flexShrink: 0,
                }}
                onMouseEnter={e => e.currentTarget.style.background = "#e0f2fe"}
                onMouseLeave={e => e.currentTarget.style.background = "#f0f9ff"}
              >
                <i className="fas fa-layer-group" style={{ fontSize: 11 }} />
                Apply All
                <span style={{ fontSize: 10, fontWeight: 800, padding: "1px 6px", borderRadius: 8, background: "#bae6fd", color: "#1e40af" }}>
                  {filteredKpis.length}
                </span>
              </button>
            )}
          </div>
          <TagFilterBar allQuestions={allKpis} filterSteps={filterSteps} onFilterStepsChange={setFilterSteps} />
        </div>

        {/* KPI table */}
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
          {/* Unsaved changes banner — sits at the very top of the table */}
          {hasPending && !isViewOnly && (
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "8px 18px",
              background: "#fffbeb", borderBottom: "1px solid #fcd34d",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                <i className="fas fa-exclamation-circle" style={{ fontSize: 12, color: "#d97706" }} />
                <span style={{ fontSize: 12, color: "#92400e", fontWeight: 600 }}>
                  {pendingMap.size} KPI{pendingMap.size !== 1 ? "s" : ""} with unsaved changes
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <button
                  onClick={() => setPendingMap(new Map())}
                  style={{
                    fontSize: 11, fontWeight: 600, color: "#b45309",
                    background: "none", border: "none", cursor: "pointer", padding: 0,
                  }}
                >
                  Discard
                </button>
                <PrimaryButton onClick={handleSaveAll} disabled={saving} style={{ minWidth: 90, padding: "5px 14px", fontSize: 12 }}>
                  {saving ? <><Spinner size={11} color="#fff" /> Saving…</> : "Save All"}
                </PrimaryButton>
              </div>
            </div>
          )}
          {/* Column headers */}
          <div style={{
            display: "grid", gridTemplateColumns: "260px 1fr",
            padding: "9px 18px", background: "#f8fafc", borderBottom: "1px solid #f1f5f9",
          }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.4px" }}>KPI Title</span>
            <span style={{ fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.4px", textAlign: "right" }}>Required Evidences (click + to add)</span>
          </div>

          {filteredKpis.length === 0 ? (
            <div style={{ padding: "60px 20px", textAlign: "center" }}>
              {allKpis.length === 0 ? (
                <>
                  <i className="fas fa-chart-bar" style={{ fontSize: 28, color: "#d1d5db", display: "block", marginBottom: 12 }} />
                  <div style={{ fontSize: 13, color: "#94a3b8" }}>
                    No active KPIs. Enable KPIs in the <strong>Active KPIs</strong> tab first.
                  </div>
                </>
              ) : (
                <div style={{ fontSize: 13, color: "#94a3b8" }}>No KPIs match the current filters.</div>
              )}
            </div>
          ) : (
            <div style={{ maxHeight: 620, overflowY: "auto" }}>
              {filteredKpis.map((kpi, idx) => (
                <KpiEvidenceRow
                  key={kpi.kpiId}
                  kpi={kpi}
                  pendingEvidences={pendingMap.has(kpi.kpiId) ? pendingMap.get(kpi.kpiId) : null}
                  onPendingChange={evs => setPendingForKpi(kpi.kpiId, evs)}
                  isLast={idx === filteredKpis.length - 1}
                  disabled={isViewOnly}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {showApplyAll && (
        <ApplyAllModal filteredKpis={filteredKpis} onClose={() => setShowApplyAll(false)} onApply={handleApplyAll} />
      )}
    </div>
  );
}